import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Paperclip, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { sprekersApi } from '../api';
import { SLIDE_TYPES, SLIDE_VISIBILITIES } from '../labels';
import type { PresentationSlide, PresentationSlideInput, SlideType, SlideVisibility } from '../types';

interface FormState {
  slide_type: SlideType;
  content: string;
  bible_reference: string;
  visibility: SlideVisibility;
  estimated_duration_seconds: string;
  media_id: string;
  media_filename: string;
}

const EMPTY_FORM: FormState = {
  slide_type: 'kernzin', content: '', bible_reference: '', visibility: 'op_beamer',
  estimated_duration_seconds: '', media_id: '', media_filename: '',
};

function toFormState(slide: PresentationSlide | null): FormState {
  if (!slide) return EMPTY_FORM;
  return {
    slide_type: slide.slide_type,
    content: slide.content || '',
    bible_reference: slide.bible_reference || '',
    visibility: slide.visibility,
    estimated_duration_seconds: slide.estimated_duration_seconds ? String(slide.estimated_duration_seconds) : '',
    media_id: slide.media_id || '',
    media_filename: slide.media_id ? 'Bijgevoegd bestand' : '',
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slide: PresentationSlide | null;
  engagementId: string;
  onSubmit: (data: PresentationSlideInput) => Promise<void>;
}

export default function SlideFormDialog({ open, onOpenChange, slide, engagementId, onSubmit }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setForm(toFormState(slide));
  }, [open, slide]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const media = await sprekersApi.uploadMedia(file, { speakingEngagementId: engagementId });
      setForm((prev) => ({ ...prev, media_id: media.id, media_filename: media.original_filename }));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Uploaden mislukt');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await onSubmit({
        slide_type: form.slide_type,
        content: form.content || null,
        bible_reference: form.bible_reference || null,
        visibility: form.visibility,
        estimated_duration_seconds: form.estimated_duration_seconds ? Number(form.estimated_duration_seconds) : null,
        media_id: form.media_id || null,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{slide ? 'Slide bewerken' : 'Nieuwe slide'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="slide-type">Type</Label>
            <Select value={form.slide_type} onValueChange={(value: SlideType) => setForm({ ...form, slide_type: value })}>
              <SelectTrigger id="slide-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SLIDE_TYPES.filter((option) => option.value !== 'vrije_slide').map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="slide-content">Inhoud</Label>
            <Textarea
              id="slide-content"
              value={form.content}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, content: e.target.value })}
              rows={4}
            />
          </div>

          <div>
            <Label htmlFor="slide-bible-reference">Bijbelverwijzing (optioneel)</Label>
            <Input
              id="slide-bible-reference"
              value={form.bible_reference}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, bible_reference: e.target.value })}
              placeholder="Bijv. Lucas 19:1-10"
            />
          </div>

          <div>
            <Label>Media (optioneel)</Label>
            {form.media_filename ? (
              <div className="mt-1 flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                <Paperclip className="h-4 w-4 text-slate-400" />
                <span className="flex-1 truncate">{form.media_filename}</span>
                <Button
                  type="button" variant="ghost" size="icon"
                  onClick={() => setForm({ ...form, media_id: '', media_filename: '' })}
                  aria-label="Media verwijderen"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </Button>
              </div>
            ) : (
              <Input ref={fileInputRef} type="file" onChange={handleFileChange} disabled={uploading} className="mt-1" />
            )}
            {uploading && <p className="mt-1 text-xs text-slate-400">Uploaden...</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="slide-visibility">Zichtbaarheid</Label>
              <Select
                value={form.visibility}
                onValueChange={(value: SlideVisibility) => setForm({ ...form, visibility: value })}
              >
                <SelectTrigger id="slide-visibility"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SLIDE_VISIBILITIES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="slide-duration">Duur (sec)</Label>
              <Input
                id="slide-duration"
                type="number"
                min={0}
                value={form.estimated_duration_seconds}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, estimated_duration_seconds: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting || uploading}>{slide ? 'Opslaan' : 'Toevoegen'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
